// Home — same layout as HomeTab.swift on iOS: greeting, your next ride on the
// chapel photo, open requests / rides available, quick actions, and
// "Upcoming rides" (which becomes the break's rides before a school break).

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { getDatabase, ref, onValue, get, query, orderByChild, equalTo } from 'firebase/database';
import { departure, displayPlace, localDay } from '../places';
import { useCurrentBreak, useSponsor, useTopDrivers } from '../liveData';
import { useBlockedUsers } from '../safety';
import './Home.css';

const MT = 'America/Denver';
const dayFmt  = new Intl.DateTimeFormat('en-US', { timeZone: MT, weekday: 'short', month: 'short', day: 'numeric' });
const timeFmt = new Intl.DateTimeFormat('en-US', { timeZone: MT, hour: 'numeric', minute: '2-digit' });
const PAST_GRACE_MS = 2 * 60 * 60 * 1000;

/** "Today", "Tomorrow", or "Sat, Oct 24". */
function friendlyDay(d) {
  const day = localDay(d);
  if (day === localDay(new Date())) return 'Today';
  if (day === localDay(new Date(Date.now() + 86400000))) return 'Tomorrow';
  return dayFmt.format(d);
}
const when = d => `${friendlyDay(d)} · ${timeFmt.format(d)}`;
const seatsOf = p => parseInt(p.seats, 10) || 0;

function greeting() {
  const h = new Date().getHours();
  return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
}

/** Upcoming posts (still listed until 2 hours after they leave), soonest first. */
function upcoming(value) {
  const cutoff = Date.now() - PAST_GRACE_MS;
  return Object.entries(value || {})
    .map(([id, v]) => ({ id, ...v, _dep: departure(v) }))
    .filter(p => p._dep && p._dep.getTime() > cutoff)
    .sort((a, b) => a._dep - b._dep);
}

const Arrow = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
  </svg>
);
const Chevron = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="9 18 15 12 9 6"/>
  </svg>
);
const Icon = {
  plus: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" aria-hidden="true"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>,
  search: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" aria-hidden="true"><circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.5" y2="16.5"/></svg>,
  hand: <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M18 11V6a2 2 0 0 0-4 0V4a2 2 0 0 0-4 0v1a2 2 0 0 0-4 0v8.5L4.6 12a2 2 0 0 0-3 2.6l4.3 5.6A6 6 0 0 0 10.7 23H14a6 6 0 0 0 6-6v-6a2 2 0 0 0-2 0z"/></svg>,
  car: <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M5 11l1.5-4.5A2 2 0 0 1 8.4 5h7.2a2 2 0 0 1 1.9 1.5L19 11a2 2 0 0 1 2 2v4a1 1 0 0 1-1 1h-1a2 2 0 0 1-4 0H9a2 2 0 0 1-4 0H4a1 1 0 0 1-1-1v-4a2 2 0 0 1 2-2zm2.1 0h9.8l-1.1-3.4a.5.5 0 0 0-.5-.3H8.7a.5.5 0 0 0-.5.3L7.1 11z"/></svg>,
};

/** true on wide screens, which get their own layout (DesktopHome). */
function useIsDesktop() {
  const mq = '(min-width: 1000px)';
  const [wide, setWide] = useState(() => window.matchMedia(mq).matches);
  useEffect(() => {
    const m = window.matchMedia(mq);
    const on = () => setWide(m.matches);
    m.addEventListener('change', on);
    return () => m.removeEventListener('change', on);
  }, []);
  return wide;
}

/** users/{uid}/name for the people on screen. */
function useNames(uids) {
  const [names, setNames] = useState({});
  const key = [...new Set(uids)].sort().join(',');
  useEffect(() => {
    if (!key) return;
    let live = true;
    const db = getDatabase();
    Promise.all(key.split(',').map(uid =>
      get(ref(db, `users/${uid}/name`)).then(s => [uid, s.val() || '']).catch(() => [uid, ''])
    )).then(pairs => { if (live) setNames(n => ({ ...n, ...Object.fromEntries(pairs) })); });
    return () => { live = false; };
  }, [key]);
  return names;
}

function Skeleton({ w, h = 14, light }) {
  return <span className={`home-skel${light ? ' light' : ''}`} style={{ width: w, height: h }} />;
}

export default function Home() {
  const navigate = useNavigate();
  const db = getDatabase();
  const me = getAuth().currentUser?.uid;
  const currentBreak = useCurrentBreak();
  const sponsor = useSponsor();
  const blocked = useBlockedUsers();

  const [firstName, setFirstName] = useState('');
  const [rides,     setRides]     = useState(null); // null while loading
  const [requests,  setRequests]  = useState(null);
  const [booked,    setBooked]    = useState([]);   // ride IDs you've booked seats on

  useEffect(() => {
    if (!me) return;
    get(ref(db, `users/${me}/name`)).then(s => setFirstName((s.val() || '').trim().split(/\s+/)[0] || '')).catch(() => {});
    const stopRides = onValue(ref(db, 'rideReserve'), s => setRides(upcoming(s.val())), () => setRides([]));
    const stopReqs  = onValue(ref(db, 'rideRequest'), s => setRequests(upcoming(s.val())), () => setRequests([]));
    const stopBooked = onValue(query(ref(db, 'bookings'), orderByChild('bookerUserID'), equalTo(me)), s => {
      const ids = [];
      s.forEach(c => { const b = c.val(); if ((b.type || 'reservation') === 'reservation') ids.push(b.rideID); });
      setBooked(ids);
    }, () => setBooked([]));
    return () => { stopRides(); stopReqs(); stopBooked(); };
  }, [db, me]);

  const loading = rides === null || requests === null;
  const visibleRides = useMemo(() => (rides || []).filter(r => !blocked.has(r.userID)), [rides, blocked]);
  // Riders who already booked a ride for their request aren't waiting anymore.
  const visibleReqs  = useMemo(() => (requests || []).filter(r => !blocked.has(r.userID) && !r.bookedRide), [requests, blocked]);

  // Your next ride: the soonest one you're driving or have a seat on.
  const nextRide = visibleRides.find(r => r.userID === me || booked.includes(r.id)) || null;
  const nextIsMine = nextRide?.userID === me;

  const openRequests = visibleReqs.filter(r => seatsOf(r) > 0 && r.userID !== me).length;
  // Other people's rides with seats left (ones you already have a seat on are in "Your next ride").
  const othersOpen   = visibleRides.filter(r => r.userID !== me && seatsOf(r) > 0 && !booked.includes(r.id));
  const stack = (currentBreak ? othersOpen.filter(r => currentBreak.contains(r)) : othersOpen)
    .slice(0, currentBreak ? 3 : 4);

  const breakRides  = currentBreak ? visibleRides.filter(r => currentBreak.contains(r)) : [];
  const breakAsking = currentBreak
    ? new Set(visibleReqs.filter(r => currentBreak.contains(r)).map(r => r.userID)).size : 0;

  const openRides = (state = {}) => navigate('/rides', { state });

  // Desktop shows more of everything: a longer ride list with drivers, and who's asking.
  const isDesktop   = useIsDesktop();
  const topDrivers  = useTopDrivers();
  const deskRides   = (currentBreak ? othersOpen.filter(r => currentBreak.contains(r)) : othersOpen).slice(0, 8);
  const askingList  = visibleReqs.filter(r => seatsOf(r) > 0 && r.userID !== me).slice(0, 5);
  const names       = useNames(isDesktop ? [...deskRides, ...askingList].map(p => p.userID) : []);

  if (isDesktop) {
    return (
      <DesktopHome {...{
        firstName, loading, nextRide, nextIsMine, currentBreak, breakRides, breakAsking,
        deskRides, totalOpen: (currentBreak ? othersOpen.filter(r => currentBreak.contains(r)) : othersOpen).length,
        askingList, openRequests, names, topDrivers, navigate, openRides, sponsor,
      }} />
    );
  }

  return (
    <div className="home-page">
      <header className="home-head">
        <div className="home-greeting">{greeting()}</div>
        <h1 className="home-name">{firstName || ' '}</h1>
      </header>

      {/* Your next ride, on the chapel */}
      <button className="home-next" onClick={() => (nextRide ? navigate('/profile') : navigate('/addreserve'))}
        style={{ backgroundImage: `linear-gradient(180deg, rgba(0,0,0,.15), rgba(0,0,0,.75)), url(${process.env.PUBLIC_URL}/chapel.jpg)` }}>
        {(loading || nextRide) && (
          <span className="home-next-label"><span className="home-next-dot">{Icon.car}</span>YOUR NEXT RIDE</span>
        )}
        <span className="home-next-body">
          {loading ? (
            <>
              <Skeleton w={190} h={20} light />
              <Skeleton w={130} h={13} light />
            </>
          ) : nextRide ? (
            <>
              <span className="home-next-route">
                {displayPlace(nextRide.fromLocation)} <Arrow /> {displayPlace(nextRide.toLocation)}
              </span>
              <span className="home-next-when">{when(nextRide._dep)}{!nextIsMine && ' · You have a seat'}</span>
              {nextIsMine && (
                <span className="home-next-pill">
                  {seatsOf(nextRide) === 0 ? 'Full' : `${seatsOf(nextRide)} seat${seatsOf(nextRide) !== 1 ? 's' : ''} open`}
                </span>
              )}
            </>
          ) : (
            <>
              <span className="home-next-route">No upcoming rides yet</span>
              <span className="home-next-when">Post one to get started.</span>
            </>
          )}
        </span>
      </button>

      {/* Two stat tiles */}
      <div className="home-stats">
        <button className="home-stat green" onClick={() => openRides({ mode: 'requests' })}>
          <span className="home-stat-bg">{Icon.hand}</span>
          {loading ? <Skeleton w={44} h={28} /> : <span className="home-stat-value">{openRequests}</span>}
          <span className="home-stat-label">open request{openRequests !== 1 ? 's' : ''}</span>
          <span className="home-stat-link">View requests <Chevron /></span>
        </button>
        <button className="home-stat blue" onClick={() => openRides({ mode: 'rides' })}>
          <span className="home-stat-bg">{Icon.car}</span>
          {loading ? <Skeleton w={44} h={28} /> : <span className="home-stat-value">{othersOpen.length}</span>}
          <span className="home-stat-label">ride{othersOpen.length !== 1 ? 's' : ''} with open seats</span>
          <span className="home-stat-link">Find a ride <Chevron /></span>
        </button>
      </div>

      {/* Quick actions */}
      <div className="home-actions">
        <button className="home-action" onClick={() => navigate('/addreserve')}>
          <span className="home-action-icon blue">{Icon.plus}</span>Post ride
        </button>
        <button className="home-action" onClick={() => navigate('/addrequest')}>
          <span className="home-action-icon green">{Icon.plus}</span>Post request
        </button>
        <button className="home-action" onClick={() => openRides({ mode: 'rides' })}>
          <span className="home-action-icon orange">{Icon.search}</span>Find a ride
        </button>
      </div>

      {/* The sponsor's ad (photo + code); free rides show in the list below */}
      {sponsor && (
        <a className="home-sponsor" href={sponsor.url} target="_blank" rel="noopener noreferrer sponsored">
          <img className="home-sponsor-img" src={sponsor.imageURL} alt="" />
          <span className="home-sponsor-text">
            <span className="home-sponsor-label">Sponsored · {sponsor.name}</span>
            <strong>{sponsor.headline}</strong>
            {sponsor.code && <span>Code <code className="home-sponsor-code">{sponsor.code}</code></span>}
          </span>
          <Chevron />
        </a>
      )}

      {/* Upcoming rides, or the break's rides before a school break */}
      <section className={`home-upcoming${currentBreak ? ' themed' : ''}`}
        style={currentBreak ? { background: currentBreak.wash, '--accent': currentBreak.accent } : undefined}>
        <div className="home-upcoming-head">
          {currentBreak ? (
            <div>
              <div className="home-break-title">
                <span aria-hidden="true">{currentBreak.icon}</span>
                <strong>{currentBreak.name}</strong>
                <span className="home-break-range">{currentBreak.range}</span>
              </div>
              <div className="home-break-tagline">{currentBreak.tagline}</div>
            </div>
          ) : (
            <>
              <h2 className="home-upcoming-title"><span aria-hidden="true">🔥</span> Upcoming rides</h2>
              <button className="home-see-all" onClick={() => openRides({ mode: 'rides' })}>See all <Chevron /></button>
            </>
          )}
        </div>

        {currentBreak && !loading && (
          <button className="home-break-summary" onClick={() => openRides({ mode: 'rides', tab: 'break' })}>
            <span>{breakRides.length} ride{breakRides.length !== 1 ? 's' : ''} · {breakAsking} asking</span>
            <span className="home-break-see">See all <Chevron /></span>
          </button>
        )}

        {loading ? (
          <div className="home-rows">
            {[0, 1, 2].map(i => (
              <div key={i} className="home-row"><span className="home-row-main"><Skeleton w={150} h={15} /><Skeleton w={110} h={12} /></span></div>
            ))}
          </div>
        ) : stack.length > 0 ? (
          <div className="home-rows">
            {stack.map(r => (
              <button key={r.id} className="home-row" onClick={() => openRides({ mode: 'rides', book: r.id })}>
                <span className="home-row-main">
                  <span className="home-row-route">{displayPlace(r.fromLocation)} <Arrow /> {displayPlace(r.toLocation)}</span>
                  <span className="home-row-when">{when(r._dep)}</span>
                </span>
                {r.sponsor
                  ? <span className="home-row-seats free">FREE</span>
                  : <span className="home-row-seats">{seatsOf(r)} seat{seatsOf(r) !== 1 ? 's' : ''}</span>}
                <span className="home-row-chevron"><Chevron /></span>
              </button>
            ))}
          </div>
        ) : (
          <div className="home-empty">
            <div>
              <div className="home-empty-title">
                {currentBreak ? 'No break rides yet' : nextRide ? 'No other rides yet' : 'No rides posted yet'}
              </div>
              <div className="home-empty-sub">
                {currentBreak ? `Driving for ${currentBreak.name}? Post early so riders find you.`
                  : nextRide ? 'Yours is the only one right now. Riders will find it on Rides.'
                  : 'Driving somewhere? Post a ride and riders will find you.'}
              </div>
            </div>
            {!(nextRide && !currentBreak) && (
              <button className="home-empty-post" onClick={() => navigate('/addreserve')}>Post</button>
            )}
          </div>
        )}
      </section>
    </div>
  );
}

/* ── Desktop ──────────────────────────────────────────────────────
   A wide chapel banner (greeting + your next ride + post buttons), then a
   two-column board: rides on the left, people asking for rides on the right. */

function DesktopHome({
  firstName, loading, nextRide, nextIsMine, currentBreak, breakRides, breakAsking,
  deskRides, totalOpen, askingList, openRequests, names, topDrivers, navigate, openRides, sponsor,
}) {
  const first = id => (names[id] || '').split(/\s+/)[0] || 'Someone';
  const summary = loading ? ' '
    : currentBreak
      ? `${breakRides.length} ride${breakRides.length !== 1 ? 's' : ''} posted and ${breakAsking} ${breakAsking === 1 ? 'person' : 'people'} asking for ${currentBreak.name}.`
      : `${totalOpen} ride${totalOpen !== 1 ? 's have' : ' has'} open seats right now.`;

  return (
    <div className="dhome">
      <section className="dhome-hero"
        style={{ backgroundImage: `linear-gradient(90deg, rgba(1,28,71,.88) 0%, rgba(1,28,71,.55) 45%, rgba(0,0,0,.25) 100%), url(${process.env.PUBLIC_URL}/chapel.jpg)` }}>
        <div className="dhome-hero-text">
          <div className="dhome-greeting">{greeting()}{firstName ? `, ${firstName}` : ''}</div>
          <h1 className="dhome-title">Where are you headed?</h1>
          <p className="dhome-summary">{summary}</p>
          <div className="dhome-hero-actions">
            <button className="dhome-btn primary" onClick={() => openRides({ mode: 'rides' })}>{Icon.search} Find a ride</button>
            <button className="dhome-btn glass" onClick={() => navigate('/addreserve')}>{Icon.plus} Post a ride</button>
            <button className="dhome-btn glass" onClick={() => navigate('/addrequest')}>{Icon.plus} Ask for a ride</button>
          </div>
        </div>

        <button className="dhome-next" onClick={() => (nextRide ? navigate('/profile') : navigate('/addreserve'))}>
          <span className="dhome-next-label">{Icon.car} Your next ride</span>
          {loading ? (
            <><Skeleton w={180} h={20} light /><Skeleton w={120} h={13} light /></>
          ) : nextRide ? (
            <>
              <span className="dhome-next-route">{displayPlace(nextRide.fromLocation)} <Arrow /> {displayPlace(nextRide.toLocation)}</span>
              <span className="dhome-next-when">{when(nextRide._dep)}</span>
              <span className="dhome-next-pill">
                {nextIsMine
                  ? (seatsOf(nextRide) === 0 ? "You're driving · Full" : `You're driving · ${seatsOf(nextRide)} open`)
                  : 'You have a seat'}
              </span>
            </>
          ) : (
            <>
              <span className="dhome-next-route">Nothing booked yet</span>
              <span className="dhome-next-when">Find a ride below, or post your own.</span>
            </>
          )}
        </button>
      </section>

      <div className="dhome-board">
        <section className={`dhome-panel dhome-rides${currentBreak ? ' themed' : ''}`}
          style={currentBreak ? { background: currentBreak.wash, '--accent': currentBreak.accent } : undefined}>
          <header className="dhome-panel-head">
            {currentBreak ? (
              <div>
                <h2><span aria-hidden="true">{currentBreak.icon}</span> {currentBreak.name}
                  <span className="dhome-break-range">{currentBreak.range}</span></h2>
                <p>{currentBreak.tagline}</p>
              </div>
            ) : (
              <div>
                <h2><span aria-hidden="true">🔥</span> Upcoming rides</h2>
                <p>Soonest first, with open seats.</p>
              </div>
            )}
            <button className="dhome-link" onClick={() => openRides(currentBreak ? { mode: 'rides', tab: 'break' } : { mode: 'rides' })}>
              See all {totalOpen > 0 ? totalOpen : ''} <Chevron />
            </button>
          </header>

          {loading ? (
            <div className="dhome-table">
              {[0, 1, 2, 3].map(i => <div key={i} className="dhome-tr"><Skeleton w={160} h={15} /><Skeleton w={120} h={13} /></div>)}
            </div>
          ) : deskRides.length === 0 ? (
            <div className="dhome-empty">
              <div>
                <strong>{currentBreak ? 'No break rides yet' : 'No other rides yet'}</strong>
                <span>{currentBreak ? `Driving for ${currentBreak.name}? Post early so riders find you.` : 'Driving somewhere? Post a ride and riders will find you.'}</span>
              </div>
              <button className="dhome-btn solid" onClick={() => navigate('/addreserve')}>Post a ride</button>
            </div>
          ) : (
            <div className="dhome-table" role="list">
              {deskRides.map(r => (
                <div key={r.id} className="dhome-tr" role="listitem">
                  <span className="dhome-route">{displayPlace(r.fromLocation)} <Arrow /> {displayPlace(r.toLocation)}</span>
                  <span className="dhome-when">{when(r._dep)}</span>
                  <span className="dhome-driver">
                    {first(r.userID)}
                    {topDrivers[r.userID] && <span className="dhome-star" title="Top driver" aria-label="Top driver">★</span>}
                  </span>
                  {r.sponsor
                    ? <span className="dhome-seats free">FREE</span>
                    : <span className="dhome-seats">{seatsOf(r)} seat{seatsOf(r) !== 1 ? 's' : ''}</span>}
                  <button className="dhome-book" onClick={() => openRides({ mode: 'rides', book: r.id })}>Book</button>
                </div>
              ))}
            </div>
          )}
        </section>

        <aside className="dhome-side">
          {sponsor && (
            <a className="dhome-panel dhome-sponsor" href={sponsor.url} target="_blank" rel="noopener noreferrer sponsored">
              <img src={sponsor.wideImageURL || sponsor.imageURL} alt="" />
              <span className="dhome-sponsor-body">
                <span className="dhome-sponsor-label">Sponsored · {sponsor.name}</span>
                <strong>{sponsor.headline}</strong>
                {sponsor.code && <span>Use code <code className="home-sponsor-code">{sponsor.code}</code></span>}
              </span>
            </a>
          )}

          <section className="dhome-panel">
            <header className="dhome-panel-head">
              <div>
                <h2 className="green">Asking for rides</h2>
                <p>{loading ? ' ' : `${openRequests} open request${openRequests !== 1 ? 's' : ''}. Driving the same way? Offer a seat.`}</p>
              </div>
            </header>
            {loading ? (
              <div className="dhome-asks"><Skeleton w={150} /><Skeleton w={120} /></div>
            ) : askingList.length === 0 ? (
              <p className="dhome-quiet">Nobody's asking right now.</p>
            ) : (
              <div className="dhome-asks">
                {askingList.map(r => (
                  <button key={r.id} className="dhome-ask" onClick={() => openRides({ mode: 'requests' })}>
                    <span className="dhome-ask-route">{displayPlace(r.fromLocation)} <Arrow /> {displayPlace(r.toLocation)}</span>
                    <span className="dhome-ask-meta">{first(r.userID)} · {when(r._dep)} · {seatsOf(r)} seat{seatsOf(r) !== 1 ? 's' : ''}</span>
                  </button>
                ))}
              </div>
            )}
            <button className="dhome-link green" onClick={() => openRides({ mode: 'requests' })}>View all requests <Chevron /></button>
          </section>

          <button className="dhome-panel dhome-borrow" onClick={() => navigate('/borrow')}>
            <span className="dhome-borrow-icon">{Icon.car}</span>
            <span>
              <strong>Need a car for the weekend?</strong>
              <span>Borrow one from someone at the Academy.</span>
            </span>
            <Chevron />
          </button>
        </aside>
      </div>
    </div>
  );
}
